import { useCallback, useEffect, useState } from 'react';
import {
  AnalyticsData,
  deleteUserData,
  exportUserData,
  getAnalytics,
  getRunnerGoal,
  getRunnerProfile,
  getStats,
  RunnerGoal,
  RunnerProfile,
  User,
  WorkoutStats
} from '../api/client';
import { RunnerProfileCard } from '../components/RunnerProfileCard';
import { RunnerGoalCard } from '../components/RunnerGoalCard';
import { ErrorState, LoadingState } from '../components/States';
import { haptic } from '../lib/telegram';
import './ProfilePage.css';

const pace = (seconds?: number | null) =>
  seconds ? `${Math.floor(seconds / 60)}′${String(Math.round(seconds % 60)).padStart(2, '0')}″` : '—';

export const ProfilePage: React.FC<{ user: User }> = ({ user }) => {
  const [profile, setProfile] = useState<RunnerProfile | null>(null);
  const [goal, setGoal] = useState<RunnerGoal | null>(null);
  const [stats, setStats] = useState<WorkoutStats | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataAction, setDataAction] = useState<'export' | 'delete' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [runnerProfile, runnerGoal, workoutStats, performance] = await Promise.all([
        getRunnerProfile(user.id), getRunnerGoal(user.id), getStats(user.id), getAnalytics(user.id)
      ]);
      setProfile(runnerProfile); setGoal(runnerGoal); setStats(workoutStats); setAnalytics(performance);
    } catch (err: any) {
      setError(err?.message || 'Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setDataAction('export');
    try { await exportUserData(user.id); haptic.success(); }
    catch (err: any) { setError(err?.message || 'Не удалось скачать данные'); haptic.error(); }
    finally { setDataAction(null); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить профиль, тренировки, планы и скриншоты без возможности восстановления?')) return;
    setDataAction('delete');
    try { await deleteUserData(user.id); window.location.reload(); }
    catch (err: any) { setError(err?.message || 'Не удалось удалить данные'); setDataAction(null); }
  };

  return (
    <main className="profile-page">
      <header className="profile-header">
        <span>RUN LAB / ATHLETE</span>
        <h1>{user.first_name || 'Профиль бегуна'}</h1>
        <p>Личные параметры, цели и ключевые показатели — в одном месте.</p>
      </header>

      {loading ? <LoadingState text="Загружаем профиль…" /> : error && !stats ? <ErrorState message={error} onRetry={load} /> : (
        <>
          <section className="profile-dashboard">
            <div><strong>{analytics?.summary.avgScore ? Math.round(analytics.summary.avgScore) : '—'}</strong><span>средний Run Score</span></div>
            <div><strong>{stats?.total || 0}</strong><span>тренировок</span></div>
            <div><strong>{analytics?.summary.distanceKm.toFixed(1) || '0'}</strong><span>км всего</span></div>
            <div><strong>{pace(analytics?.summary.avgPaceSecPerKm)}</strong><span>средний темп</span></div>
          </section>

          <details className="profile-disclosure" open>
            <summary><div><span>01</span><strong>Данные спортсмена</strong><small>Для персонального скоринга</small></div><i /></summary>
            <div className="disclosure-content">
              <RunnerProfileCard userId={user.id} profile={profile} onSaved={saved => { setProfile(saved); load(); }} />
            </div>
          </details>

          <details className="profile-disclosure">
            <summary><div><span>02</span><strong>Цель и расписание</strong><small>Определяет направление AI-плана</small></div><i /></summary>
            <div className="disclosure-content">
              <RunnerGoalCard userId={user.id} goal={goal} onSaved={saved => { setGoal(saved); load(); }} />
            </div>
          </details>

          <details className="profile-disclosure">
            <summary><div><span>03</span><strong>Текущая форма</strong><small>Нагрузка и выполнение плана</small></div><i /></summary>
            <div className="profile-form-metrics">
              <div><span>Эта неделя</span><strong>{analytics?.trainingLoad.currentDistanceKm || 0} км</strong></div>
              <div><span>Нагрузка</span><strong>{analytics?.trainingLoad.status || '—'}</strong></div>
              <div><span>Выполнение</span><strong>{analytics?.planAdherence.score ?? '—'}/100</strong></div>
              <div><span>Лучший скор</span><strong>{analytics?.personalRecords.bestScore?.score ?? '—'}</strong></div>
            </div>
          </details>

          <details className="profile-disclosure">
            <summary><div><span>04</span><strong>Данные и приватность</strong><small>Экспорт или полное удаление</small></div><i /></summary>
            <div className="profile-data-actions">
              <p>Скачайте архив тренировок или удалите профиль вместе со всеми исходными отчётами.</p>
              <button onClick={handleExport} disabled={dataAction !== null}>{dataAction === 'export' ? 'Готовим архив…' : 'Скачать мои данные'}</button>
              <button className="danger" onClick={handleDelete} disabled={dataAction !== null}>{dataAction === 'delete' ? 'Удаляем…' : 'Удалить все данные'}</button>
            </div>
          </details>
          {error && <p className="profile-inline-error">{error}</p>}
        </>
      )}
    </main>
  );
};
